import { useCallback, useRef, useState } from 'react'
import { useAtomValue } from 'jotai'
import { currentMessagesAtom, currentSessionIdAtom, currentHasMoreAtom } from '../state/store'
import { fetchMessages } from '../api/backend'
import { backendMessageToChat } from '../state/types'
import { useAuth } from '../contexts/AuthContext'
import { useSessions } from './useSessions'

/**
 * Hook for loading older messages when user scrolls to the top.
 *
 * Returns `loadMore` (trigger), `isLoadingMore` (spinner state),
 * `hasMore`, and `loadingRef` for synchronous guard checks.
 */
export function useLoadMoreMessages() {
  const { getAccessToken } = useAuth()
  const { prependMessages } = useSessions()
  const sessionId = useAtomValue(currentSessionIdAtom)
  const messages = useAtomValue(currentMessagesAtom)
  const hasMore = useAtomValue(currentHasMoreAtom)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Synchronous guard — readable from scroll handlers without waiting
  // for React state updates.
  const loadingRef = useRef(false)

  const loadMore = useCallback(async () => {
    if (!sessionId || !hasMore || loadingRef.current) return
    const token = getAccessToken()
    if (!token) return

    const earliest = messages[0]
    if (!earliest) return

    loadingRef.current = true
    setIsLoadingMore(true)
    try {
      const result = await fetchMessages(sessionId, token, {
        before: earliest.id,
        limit: 10,
      })
      const older = result.messages.map(backendMessageToChat)

      // If the API returned 0 new messages, or all returned messages
      // already exist locally (duplicates), there's nothing more to load.
      const existingIds = new Set(messages.map((m) => m.id))
      const genuinelyNew = older.filter((m) => !existingIds.has(m.id))
      const effectiveHasMore = genuinelyNew.length > 0 && result.hasMore

      prependMessages(sessionId, older, effectiveHasMore)
    } catch (err) {
      console.error('Failed to load more messages:', err)
    } finally {
      loadingRef.current = false
      setIsLoadingMore(false)
    }
  }, [sessionId, hasMore, messages, getAccessToken, prependMessages])

  return { loadMore, isLoadingMore, hasMore, loadingRef }
}
